import React, { useState, useEffect, useCallback } from "react";
import Base from "../core/Base";
import {
  Box, Typography, Paper, Grid, Chip, LinearProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Skeleton, Tooltip, IconButton, Alert, Divider,
} from "@mui/material";
import RefreshIcon       from "@mui/icons-material/Refresh";
import CheckCircleIcon   from "@mui/icons-material/CheckCircle";
import ErrorIcon         from "@mui/icons-material/Error";
import StorageIcon       from "@mui/icons-material/Storage";
import SpeedIcon         from "@mui/icons-material/Speed";
import PeopleIcon        from "@mui/icons-material/People";
import { isAutheticated }  from "../auth/helper";
import { getRedisMonitor } from "./helper/adminapicall";

const MetricCard = ({ icon, label, value, sub, color = "primary.main", loading }) => (
  <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3, height: "100%" }}>
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
      <Box>
        <Typography variant="caption" color="text.secondary" fontWeight={500}>{label}</Typography>
        <Typography variant="h5" fontWeight={700} sx={{ mt: 0.5 }}>
          {loading ? <Skeleton width={60} /> : value}
        </Typography>
        {sub && <Typography variant="caption" color="text.disabled">{sub}</Typography>}
      </Box>
      <Box sx={{ width: 40, height: 40, borderRadius: "10px", bgcolor: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
        {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
      </Box>
    </Box>
  </Paper>
);

const InfoRow = ({ label, value }) => (
  <Box sx={{ display: "flex", py: 0.8, borderBottom: "1px solid", borderColor: "divider" }}>
    <Typography variant="caption" color="text.secondary" sx={{ width: 180, flexShrink: 0 }}>{label}</Typography>
    <Typography variant="caption" sx={{ fontFamily: "monospace", fontSize: "0.8rem" }}>{value ?? "—"}</Typography>
  </Box>
);

const RedisMonitor = () => {
  const { admin, token } = isAutheticated();
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState("");
  const [ts,      setTs]      = useState(null);

  const load = useCallback(() => {
    setLoading(true); setError("");
    getRedisMonitor(admin._id, token)
      .then((d) => {
        if (d?.error) setError(d.error);
        else { setData(d); setTs(new Date()); }
      })
      .catch(() => setError("Failed to fetch Redis info"))
      .finally(() => setLoading(false));
  }, [admin._id, token]);

  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, [load]);

  const info    = data?.server    || data?.info    || {};
  const mem     = data?.memory    || {};
  const clients = data?.clients   || {};
  const stats   = data?.stats     || {};
  const streams = data?.streams   || [];
  const keys    = data?.keys      || {};
  const health  = data?.health    || {};

  const healthOk   = health.status === "ok";
  const healthWarn = health.status === "warn";

  const usedMem  = mem.used_memory       || mem.usedMemory       || 0;
  const maxMem   = mem.maxmemory         || mem.maxMemory         || 0;
  const memPct   = maxMem > 0 ? Math.min((usedMem / maxMem) * 100, 100) : 0;

  return (
    <Base>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2 }}>
        <Box>
          <Typography variant="h5">Redis Monitor</Typography>
          <Typography variant="body2" color="text.secondary">
            Cache health, memory usage, and stream consumer groups
            {ts && <> · {ts.toLocaleTimeString()}</>}
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {!loading && data && (
            <Chip
              icon={
                healthOk   ? <CheckCircleIcon sx={{ fontSize: "14px !important" }} /> :
                healthWarn ? <SpeedIcon       sx={{ fontSize: "14px !important" }} /> :
                             <ErrorIcon       sx={{ fontSize: "14px !important" }} />
              }
              label={health.status ? health.status.toUpperCase() : "UNKNOWN"}
              size="small"
              color={healthOk ? "success" : healthWarn ? "warning" : "error"}
              variant="outlined"
            />
          )}
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={load} disabled={loading}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Metric cards */}
      <Grid container spacing={2.5} sx={{ mb: 3 }}>
        <Grid item xs={6} md={3}>
          <MetricCard
            icon={<StorageIcon />} label="Memory Used"
            value={usedMem ? `${(usedMem / 1024 / 1024).toFixed(1)} MB` : "—"}
            sub={maxMem ? `of ${(maxMem / 1024 / 1024).toFixed(0)} MB limit` : "no limit"}
            color="#3b82f6" loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            icon={<PeopleIcon />} label="Connected Clients"
            value={clients.connected_clients ?? clients.connectedClients ?? "—"}
            sub="active connections"
            color="#10b981" loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            icon={<SpeedIcon />} label="Ops/sec"
            value={stats.instantaneous_ops_per_sec ?? stats.opsPerSec ?? "—"}
            sub="commands processed"
            color="#f59e0b" loading={loading}
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <MetricCard
            icon={<CheckCircleIcon />} label="Uptime"
            value={info.uptime_in_seconds
              ? `${Math.floor(info.uptime_in_seconds / 86400)}d ${Math.floor((info.uptime_in_seconds % 86400) / 3600)}h`
              : "—"}
            sub="since last restart"
            color="#8b5cf6" loading={loading}
          />
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Memory bar */}
        {maxMem > 0 && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                <Typography variant="subtitle2" fontWeight={600}>Memory Usage</Typography>
                <Typography variant="caption" color="text.secondary">{memPct.toFixed(1)}%</Typography>
              </Box>
              <LinearProgress
                variant="determinate" value={memPct}
                color={memPct > 85 ? "error" : memPct > 65 ? "warning" : "primary"}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Paper>
          </Grid>
        )}

        {/* Server info */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Server Info</Typography>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />)
            ) : (
              <>
                <InfoRow label="Redis version"  value={info.redis_version || info.redisVersion} />
                <InfoRow label="Mode"           value={info.redis_mode || info.mode || "standalone"} />
                <InfoRow label="OS"             value={info.os} />
                <InfoRow label="TCP port"       value={info.tcp_port || info.port} />
                <InfoRow label="Total commands" value={(info.total_commands_processed || info.totalCommandsProcessed || 0).toLocaleString()} />
              </>
            )}
          </Paper>
        </Grid>

        {/* Key inventory */}
        <Grid item xs={12} md={6}>
          <Paper elevation={0} sx={{ p: 2.5, border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Key Inventory</Typography>
            {loading ? (
              Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} height={28} sx={{ mb: 0.5 }} />)
            ) : Object.keys(keys).length === 0 ? (
              <Typography variant="body2" color="text.secondary">No key inventory data</Typography>
            ) : (
              Object.entries(keys).map(([k, v]) => <InfoRow key={k} label={k} value={String(v)} />)
            )}
          </Paper>
        </Grid>

        {/* Streams */}
        {(loading || streams.length > 0) && (
          <Grid item xs={12}>
            <Paper elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: "1px solid", borderColor: "divider" }}>
                <Typography variant="subtitle2" fontWeight={600}>Stream Consumer Groups</Typography>
              </Box>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {["Stream", "Group", "Consumers", "Pending", "Last Delivered"].map((h) => (
                        <TableCell key={h}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: 3 }).map((_, i) => (
                        <TableRow key={i}>
                          {Array.from({ length: 5 }).map((__, j) => (
                            <TableCell key={j}><Skeleton height={20} /></TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : streams.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>
                          No streams configured
                        </TableCell>
                      </TableRow>
                    ) : streams.map((s, i) => (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontFamily: "monospace", fontSize: "0.78rem", color: "primary.light" }}>{s.stream}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{s.name}</TableCell>
                        <TableCell>{s.consumers}</TableCell>
                        <TableCell>
                          <Chip
                            label={s.pending || 0}
                            size="small"
                            color={s.pending > 0 ? "warning" : "default"}
                            sx={{ fontSize: "0.7rem" }}
                          />
                        </TableCell>
                        <TableCell sx={{ color: "text.secondary", fontSize: "0.78rem" }}>{s.lastDeliveredId || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Base>
  );
};

export default RedisMonitor;
